/**
 * Template Service - API client for template management
 * Connects to the backend Template Engine from TASK-009
 */

import axios, { AxiosInstance } from 'axios';
import { API_CONFIG } from '../config/api-config';
import { httpClient } from '../lib/httpClient';

// Types for template system
export interface FieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minValue?: number;
  maxValue?: number;
  allowedValues?: string[];
  customValidator?: string;
}

export interface FormField {
  code: string;
  type: 'text' | 'email' | 'number' | 'date' | 'datetime' |
        'select' | 'multiselect' | 'checkbox' | 'radio' |
        'textarea' | 'file' | 'signature' | 'currency' |
        'password' | 'tel' | 'url';
  label: string;
  placeholder?: string;
  helpText?: string;
  defaultValue?: any;
  validation?: FieldValidation;
  prefillSource?: string;
  dependsOn?: string;
  options?: Array<{ value: string; label: string }>;
  position: number;
  columnSpan?: number;
  visible?: boolean;
  readonly?: boolean;
}

export interface FormSection {
  title: string;
  description?: string;
  fields: FormField[];
  collapsible?: boolean;
  collapsed?: boolean;
  visible?: boolean;
}

export interface FormSchema {
  code: string;
  title: string;
  description?: string;
  sections: FormSection[];
  submitButtonText?: string;
  saveDraftEnabled?: boolean;
  validationOnBlur?: boolean;
  showProgress?: boolean;
  schemaDefinition?: FormSchema; // Allow nested schema for type compatibility with Template
}

export interface Template {
  id: string;
  code: string;
  type: 'form' | 'pdf' | 'email' | 'report';
  category?: 'kyc' | 'compliance' | 'portfolio' | 'transaction' | 'brief' | 'proposal';
  version: number;
  templateContent: string;
  schemaDefinition?: FormSchema;
  active: boolean;
  translations?: Record<string, Record<string, string>>;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateListResponse {
  templates: Template[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RenderRequest {
  data: Record<string, any>;
  outputFormat: 'html' | 'pdf' | 'json';
  language: string;
  preview?: boolean;
}

export interface PrefillRequest {
  client_id: string;  // Backend expects snake_case
  include_proposals?: boolean;
  include_portfolio?: boolean;
  language?: 'en' | 'de' | 'fr' | 'it';
  proposal_ids?: string[];  // Optional proposal IDs for transaction forms
  proposals?: any[];  // Full proposal data with transactions
}

export interface PrefillResponse {
  template_code: string;  // Backend returns snake_case
  prefilled_data: Record<string, any>;
  confidence_scores: Record<string, number>;
  warnings?: string[];
}

class TemplateService {
  private api: AxiosInstance;
  private cache: Map<string, { template: Template; timestamp: number }> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Use centralized API configuration
    const baseURL = API_CONFIG.GATEWAY_URL;

    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for auth token if needed
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get a template by code
   */
  async getTemplate(
    code: string,
    options?: {
      version?: number;
      includeTranslations?: boolean;
      useCache?: boolean;
    }
  ): Promise<Template> {
    const { version, includeTranslations = true, useCache = true } = options || {};

    // Check cache first
    if (useCache) {
      const cached = this.cache.get(code);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.template;
      }
    }

    try {
      const params = new URLSearchParams();
      if (version) params.append('version', version.toString());
      if (includeTranslations) params.append('include_translations', 'true');

      const response = await this.api.get<Template>(
        `/api/templates/${code}?${params.toString()}`
      );

      // Cache the result
      if (useCache) {
        this.cache.set(code, {
          template: response.data,
          timestamp: Date.now(),
        });
      }

      return response.data;
    } catch (error) {
      console.error(`Failed to fetch template ${code}:`, error);
      throw error;
    }
  }

  /**
   * List all templates with optional filters
   */
  async listTemplates(options?: {
    type?: string;
    category?: string;
    active?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<TemplateListResponse> {
    try {
      const response = await this.api.get<TemplateListResponse>('/api/templates', {
        params: options,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to list templates:', error);
      throw error;
    }
  }

  /**
   * Render a template with data
   */
  async renderTemplate(
    code: string,
    request: RenderRequest
  ): Promise<Blob | string | Record<string, any>> {
    try {
      const response = await this.api.post(
        `/api/templates/${code}/render`,
        request,
        {
          responseType: request.outputFormat === 'pdf' ? 'blob' : 'json',
        }
      );

      return response.data;
    } catch (error) {
      console.error(`Failed to render template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Preview a template with sample data
   */
  async previewTemplate(code: string, language = 'en'): Promise<string> {
    try {
      const response = await this.api.get<string>(
        `/api/templates/${code}/preview`,
        {
          params: { language },
          responseType: 'text',
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to preview template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Prefill a template with client data using AI
   */
  async prefillTemplate(
    code: string,
    request: PrefillRequest
  ): Promise<PrefillResponse> {
    try {
      // Use httpClient for automatic auth header injection
      const response = await httpClient.post<PrefillResponse>(
        `/api/templates/${code}/prefill`,
        request
      );
      // httpClient returns data directly, not wrapped in response
      return response;
    } catch (error) {
      console.error(`Failed to prefill template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Create a new template
   */
  async createTemplate(template: Partial<Template>): Promise<Template> {
    try {
      const response = await this.api.post<Template>('/api/templates', template);
      // Clear cache as templates have changed
      this.cache.clear();
      return response.data;
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(
    code: string,
    updates: Partial<Template>,
    createVersion = false
  ): Promise<Template> {
    try {
      const response = await this.api.put<Template>(
        `/api/templates/${code}`,
        updates,
        {
          params: { create_version: createVersion },
        }
      );
      // Clear cache for this template
      this.cache.delete(code);
      return response.data;
    } catch (error) {
      console.error(`Failed to update template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Delete a template (soft delete)
   */
  async deleteTemplate(code: string, version?: number): Promise<void> {
    try {
      const params = version ? { version } : {};
      await this.api.delete(`/api/templates/${code}`, { params });
      // Clear cache for this template
      this.cache.delete(code);
    } catch (error) {
      console.error(`Failed to delete template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Clone an existing template
   */
  async cloneTemplate(code: string, newCode: string): Promise<Template> {
    try {
      const response = await this.api.post<Template>(
        `/api/templates/${code}/clone`,
        null,
        {
          params: { new_code: newCode },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to clone template ${code}:`, error);
      throw error;
    }
  }

  /**
   * Clear the template cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Generate a PDF from form data
   */
  async generatePDF(
    templateCode: string,
    formData: Record<string, any>,
    language = 'en'
  ): Promise<Blob> {
    const pdfBlob = await this.renderTemplate(templateCode, {
      data: formData,
      outputFormat: 'pdf',
      language,
    }) as Blob;

    return pdfBlob;
  }

  /**
   * Save form draft to backend (if implemented)
   * For now, returns a mock response
   */
  async saveDraft(
    templateCode: string,
    clientId: string,
    draftData: Record<string, any>
  ): Promise<{ draftId: string; savedAt: string }> {
    // TODO: Implement when backend supports drafts
    console.log('Saving draft:', { templateCode, clientId, draftData });
    return {
      draftId: `draft-${Date.now()}`,
      savedAt: new Date().toISOString(),
    };
  }

  /**
   * Load form draft from backend (if implemented)
   * For now, returns null
   */
  async loadDraft(
    templateCode: string,
    clientId: string
  ): Promise<Record<string, any> | null> {
    // TODO: Implement when backend supports drafts
    console.log('Loading draft:', { templateCode, clientId });
    return null;
  }
}

// Export singleton instance
export const templateService = new TemplateService();

// Export class for testing
export default TemplateService;